import {createApp} from 'vue';

import App from '@/App.vue';

import {createRouter, createWebHistory} from 'vue-router/dist/vue-router';
import axios from 'axios';
import envService from '@/services/env.service';
import userService from '@/services/user.service';

import {HomeRoutes} from '@/modules/home/router';
import {LoginRoutes} from '@/modules/login/router';
import {InfoRoutes} from '@/modules/info/router';
import {CampaignsRoutes} from '@/modules/campaigns/router';
import {SendSmsRoutes} from '@/modules/sendsms/router';
import {SmsOutboxRoutes} from '@/modules/smsoutbox/router';
import {SubscriptionDetailsRoutes} from '@/modules/subscription/router';
import {BillingRoutes} from '@/modules/billing/router';
import {StatsRoutes} from './modules/statistics/router';
import {SmsInboxRoutes} from '@/modules/smsinbox/router';
import {AllowedPrefixesRouter} from "@/modules/allowList/router";
import {DeniedPrefixRoutes} from '@/modules/blockList/router';
import {UnicodeDetectorRoutes} from "@/modules/unicodeDetector/router";
import {RulesRoutes} from "@/modules/rules/router";
import {BillingStateRoutes} from "@/modules/billingStatus/router";


import {i18n} from '@/i18n/i18n';

import {defineCustomElements} from '@swisscom/sdx/dist/js/webcomponents/loader';

import '@swisscom/sdx/dist/css/webcomponents.css';
import '@swisscom/sdx/dist/css/sdx.min.css';
import '@swisscom/sdx/dist/js/sdx'
import loadingSpinnerService from "@/services/loading-spinner.service";
import {logger, vlOptions, VueLogger, isProduction} from '@/services/logger.service.js'
import {showAlert} from "@/services/notification.service";

defineCustomElements();

if (envService.isDevMockEnv()) {
    import('@/mock/server.js')
        .then((mockServer) => {
            mockServer.startMockServer();
        })
}

const routesDefinition = [
    ...HomeRoutes,
    ...LoginRoutes,
    ...InfoRoutes,
    ...SendSmsRoutes,
    ...CampaignsRoutes,
    ...SmsOutboxRoutes,
    ...StatsRoutes,
    ...BillingRoutes,
    ...SmsInboxRoutes,
    ...RulesRoutes,
    ...UnicodeDetectorRoutes,
    ...AllowedPrefixesRouter,
    ...DeniedPrefixRoutes,
    ...SubscriptionDetailsRoutes,
    ...BillingStateRoutes,
];

const router = createRouter({
    history: createWebHistory(envService.getBaseUrl()),
    routes: routesDefinition,
});

router.beforeEach((to, from, next) => {
    const publicPages = [
        '/',
        '/api-products',
        '/campaigns/quickguide',
        '/unicode-detector',
        '/health',
        '/login',
        '/login/swisscom',
        '/login/swisscomlocalhost',
        '/login/dm',
        '/logout',
        '/post-logout'
    ];
    const authRequired = !publicPages.includes(to.path);
    const user = userService.getLoggedInUser();

    if (authRequired && !user) {
        userService.clearUserData();
        window.location = '/login';
        localStorage.setItem('returnUrl', to.fullPath);
    }

    next();
});

// XMLHttpRequest from a different domain cannot set cookie values for their own domain unless
// withCredentials is set to true before making the request
axios.defaults.withCredentials = true;

axios.interceptors.request.use(
    request => {
        let csrfToken = document.cookie.match('(^|;)\\s*' + 'XSRF-TOKEN' + '\\s*=\\s*([^;]+)')?.pop() || '';
        if (csrfToken) {
            request.headers['X-CSRF-TOKEN'] = csrfToken;
        } else {
            if (request.method != "GET" && request.method != "get" && !request.url.endsWith("/csrf"))
                console.error(request.method + ": CSRF token not found in Cookies.")
        }

        logger.debug("URL: " + request.url)
        if (request.data !== undefined)
            logger.debug("Request: " + JSON.stringify(request.data))
        return request;
    },
    (request, response) => {
        logger.debug("URL: " + request.url)
        if (request.data !== undefined)
            logger.debug("Request: " + JSON.stringify(request.data))
        if (response.data !== undefined)
            logger.debug("Response: " + response.status + ": " + JSON.stringify(response.data))
        return request
    }
);

axios.interceptors.response.use(
    (response) => {
        if (response.data !== undefined)
            logger.debug("Response: " + response.status + ": " + JSON.stringify(response.data))
        return response;
    },
    (error) => {
        if (("" + error.request?.responseURL).endsWith("/csrf")) {
            logger.error("Ignore Error Logging for CSRF.")
        } else {
            if (error.response) {
                if (!isProduction && error.response.data !== undefined)
                    logger.debug("Error-Response: " + JSON.stringify(error.response.data))
                var errorText = "API Error: " + error.response?.status //+ " / " + error.response?.statusText
                /*if (error.response?.data?.status)
                    errorText = errorText + " / Status: " + error.response?.data?.status*/
                if (error.response?.data?.message)
                    errorText = errorText + " / " + error.response?.data?.message
                /*if (!isProduction && error.response?.data?.code)
                    errorText = errorText + " / Code: " + error.response?.data?.code*/
                if (!isProduction && error.response?.data?.detail)
                    errorText = errorText + " / Detail: " + error.response?.data?.detail
                switch (error.response?.status) {
                    case 400:
                        logger.error(errorText + " | " + error)
                        break;
                    case 401:
                    case 403:
                        showAlert(errorText + " => Logout!!!")
                        logger.error(errorText + " | " + error)
                        userService.removeLoggedInUser();
                        loadingSpinnerService.hideAll();
                        window.location = '/login';
                        break;
                    // TODO handle other errors in order to show a notification
                    default:
                        showAlert(errorText)
                        logger.error(errorText + " | " + error)
                }
            }
        }
        return Promise.reject(error);
    }
);

const app = createApp(App);
app.use(router).use(i18n).use(VueLogger, vlOptions);
app.mount('#app');

// eslint-disable-next-line no-unused-vars
window.onerror = function (message, source, lineno, colno, error) {
    // TODO: write any custom logic or logs the error
    logger.error("Message: " + message + " | " + "Error: " + error)
    return true;
};
